import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";
import Orders from "@/pages/Orders";
import ImageDashboard from "@/pages/ImageDashboard";
import TrashPage from "@/pages/TrashPage";
import TemplatesPage from "@/pages/TemplatesPage";
import ProductionPage from "@/pages/ProductionPage";
import OutsourcedPage from "@/pages/OutsourcedPage";
import LoginPage from "@/pages/LoginPage";
// import SignupPage from "@/pages/SignupPage"; // Signup disabled
import AdminUsersPage from "@/pages/AdminUsersPage";
import ContactsPage from "@/pages/ContactsPage";
import AIAgentPage from "@/pages/AIAgentPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import DropdownManagerPage from "@/pages/DropdownManagerPage";
import axios from "axios";

// Global axios interceptor - auto redirect to login on 401 (token expired)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Helper to check if user has permission
function hasPermission(user, permission) {
  if (!user) return false;
  if (user.role === "admin") return true;
  const permissions = user.permissions || [];
  return permissions.includes(permission) || permissions.includes("all");
}

// Protected Route wrapper - redirects to login if not authenticated
function ProtectedRoute({ children }) {
  const token = localStorage.getItem("token");
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// Permission-based Route wrapper
function PermissionRoute({ children, permission }) {
  const token = localStorage.getItem("token");
  const userStr = localStorage.getItem("user");
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  if (userStr) {
    const user = JSON.parse(userStr);
    if (!hasPermission(user, permission)) {
      return <Navigate to="/login" replace />;
    }
  }
  
  return children;
}

// Admin Route wrapper - requires admin role
function AdminRoute({ children }) {
  const token = localStorage.getItem("token");
  const userStr = localStorage.getItem("user");
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  if (userStr) {
    const user = JSON.parse(userStr);
    if (user.role !== "admin") {
      return <Navigate to="/login" replace />;
    }
  }
  
  return children;
}

// Public Route wrapper - redirects to first available page if already logged in
function PublicRoute({ children }) {
  const token = localStorage.getItem("token");
  const userStr = localStorage.getItem("user");
  
  if (token && userStr) {
    const user = JSON.parse(userStr);
    // Redirect to first available page based on permissions
    if (user.role === "admin" || hasPermission(user, "support")) {
      return <Navigate to="/dashboard" replace />;
    } else if (hasPermission(user, "orders")) {
      return <Navigate to="/orders" replace />;
    } else if (hasPermission(user, "images")) {
      return <Navigate to="/images" replace />;
    } else if (hasPermission(user, "production")) {
      return <Navigate to="/production" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          {/* Signup route disabled */}
          
          {/* Protected routes with permissions */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/support" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<PermissionRoute permission="support"><Dashboard /></PermissionRoute>} />
          <Route path="/orders" element={<PermissionRoute permission="orders"><Orders /></PermissionRoute>} />
          <Route path="/images" element={<PermissionRoute permission="images"><ImageDashboard /></PermissionRoute>} />
          <Route path="/trash" element={<PermissionRoute permission="images"><TrashPage /></PermissionRoute>} />
          <Route path="/templates" element={<PermissionRoute permission="templates"><TemplatesPage /></PermissionRoute>} />
          <Route path="/production" element={<PermissionRoute permission="production"><ProductionPage /></PermissionRoute>} />
          <Route path="/outsourced" element={<PermissionRoute permission="production"><OutsourcedPage /></PermissionRoute>} />
          <Route path="/analytics" element={<PermissionRoute permission="orders"><AnalyticsPage /></PermissionRoute>} />
          <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
          
          {/* Admin only routes */}
          <Route path="/settings" element={<AdminRoute><Settings /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
          <Route path="/ai-agent" element={<AdminRoute><AIAgentPage /></AdminRoute>} />
          <Route path="/dropdown-manager" element={<AdminRoute><DropdownManagerPage /></AdminRoute>} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors duration={2000} />
    </div>
  );
}

export default App;
